import { ApiError } from '@trackizy/types';
import type { InternalRequest } from '../client.js';
import type { StaffRole } from '@trackizy/types';
import type { RNFileLike } from './storage.js';

export interface MyWorkResponse {
  routes: unknown[];
  tasks: unknown[];
  jobs: unknown[];
}

export interface LocationBody {
  lat?: number;
  lon?: number;
}

/** Declares that this client captures attendance photos (spec §8.2): the
 *  server enforces requireClockInPhoto only for callers that send it, so
 *  installed Staff 1.0.0 builds keep clocking in. */
export const ATTENDANCE_PHOTO_CAPABILITY = 'attendance_photo';
/** Declares that this client sends a completion location (spec §6): with it and
 *  no fix the server stores `completed_location_status: 'not_recorded'`. */
export const STOP_LOCATION_CAPABILITY = 'stop_location';
/** Declares best-effort SOS location (spec §5). Informational on the server. */
export const SOS_LOCATION_CAPABILITY = 'sos_location';
/** Names match server/src/lib/client-capabilities.ts; the server reads the
 *  union of this comma-separated header and the body `capabilities` array. */
export const CAPABILITIES_HEADER = 'X-Trackizy-Capabilities';

export interface PunchBody {
  lat?: number;
  lon?: number;
  accuracy_m?: number;
  photo_id?: string;
  /** ISO instant of the tap — the server accepts now−72h … now+2min. */
  occurred_at?: string;
  /** Idempotency key: a repeat returns the existing row with 200. */
  client_id?: string;
}

export interface ClockInBody extends PunchBody {
  capabilities?: string[];
}

export type ClockOutBody = PunchBody;

export interface BreakBody {
  occurred_at?: string;
  client_id?: string;
}

export interface AttendanceRecord {
  id: string;
  clocked_in_at: string;
  clocked_out_at: string | null;
  auto_closed?: boolean;
  break_minutes?: number | null;
}

/** Plan A `todayShift()`: the scheduled shift covering today, ISO instants. */
export interface AttendanceToday {
  shift_name: string;
  start_at: string;
  end_at: string;
}

export interface AttendanceCurrentResponse {
  attendance: AttendanceRecord | null;
  today?: AttendanceToday | null;
  open_break?: { started_at: string } | null;
  photo_required?: boolean;
}

export interface AttendancePhotoResponse {
  photo: { id: string };
}

/** Clock-in/out and break start/end all answer `{ attendance }` (worker-redacted). */
export interface AttendancePunchResponse {
  attendance: AttendanceRecord;
}

/**
 * Every `error` code POST /staff/attendance/photos can answer
 * (server/src/staff/attendance.routes.ts). 400: multipart_required,
 * file_required, invalid_kind, invalid_client_id, invalid_multipart (too many
 * files/fields/parts); 409 client_id_conflict; 413 photo_too_large;
 * 415 unsupported_type; 429 rate_limited (20 per user per 10 min).
 * Only 429 (with transport errors, timeouts, 408 and 5xx) is retryable — the
 * app owns the classifier (Staff `queueRetry.isFreeRetry`, ruling B8).
 */
export const ATTENDANCE_PHOTO_ERROR_CODES = [
  'multipart_required',
  'file_required',
  'invalid_kind',
  'invalid_client_id',
  'invalid_multipart',
  'client_id_conflict',
  'photo_too_large',
  'unsupported_type',
  'rate_limited',
] as const;
export type AttendancePhotoErrorCode = (typeof ATTENDANCE_PHOTO_ERROR_CODES)[number];

export function staffMyWorkEndpoints(req: InternalRequest) {
  return {
    /**
     * GET /staff/me/work — aggregated routes, tasks, and jobs.
     * Optional routeStatuses filters routes by status. Server defaults to
     * ['draft','active'] (Today-screen contract). Pass a broader list for
     * the Dispatch tab's status pills.
     */
    list: (params?: { routeStatuses?: string[] }) => {
      const qs =
        params?.routeStatuses && params.routeStatuses.length > 0
          ? '?routeStatuses=' + encodeURIComponent(params.routeStatuses.join(','))
          : '';
      return req.request<MyWorkResponse>('/staff/me/work' + qs);
    },
  };
}

export function staffShiftsEndpoints(req: InternalRequest) {
  return {
    /** GET /staff/shifts/current — fetch the currently active shift record. */
    current: () => req.request<unknown>('/staff/shifts/current'),
    /** POST /staff/shifts/start — start a new shift, optionally recording location. */
    start: (body?: LocationBody) =>
      req.request<unknown>('/staff/shifts/start', {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
    /** POST /staff/shifts/end — end the active shift, optionally recording location. */
    end: (body?: LocationBody) =>
      req.request<unknown>('/staff/shifts/end', {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
  };
}

export function staffAttendanceEndpoints(req: InternalRequest) {
  const post = (path: string, body: unknown, headers?: Record<string, string>) =>
    req.request<AttendancePunchResponse>(path, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
      ...(headers ? { headers } : {}),
    });

  return {
    /** GET /staff/attendance/current — `{ attendance, today, open_break, photo_required }`. */
    current: () => req.request<AttendanceCurrentResponse>('/staff/attendance/current'),
    /** POST /staff/attendance/clock-in. Capabilities ride the body AND a header.
     *  400 invalid_occurred_at, 409 already_open | overlap | client_id_conflict,
     *  422 photo_required (only when attendance_photo is declared). */
    clockIn: (body?: ClockInBody) =>
      post(
        '/staff/attendance/clock-in',
        body,
        body?.capabilities?.length ? { [CAPABILITIES_HEADER]: body.capabilities.join(',') } : undefined,
      ),
    /** POST /staff/attendance/clock-out — 404 no_open_row, 400 invalid_occurred_at, 409 client_id_conflict. */
    clockOut: (body?: ClockOutBody) => post('/staff/attendance/clock-out', body),
    /** POST /staff/attendance/break/start — 409 not_clocked_in | break_open. */
    breakStart: (body?: BreakBody) => post('/staff/attendance/break/start', body),
    /** POST /staff/attendance/break/end — 409 no_open_break. */
    breakEnd: (body?: BreakBody) => post('/staff/attendance/break/end', body),
    /**
     * POST /staff/attendance/photos — private attendance photo (stored in
     * Postgres, never under /uploads). The server accepts parts in any order;
     * text fields go first anyway. 201 on create, 200 with the same id on a
     * repeated client_id. Errors (`ApiError.code`, see
     * ATTENDANCE_PHOTO_ERROR_CODES): 400 multipart_required | file_required |
     * invalid_kind | invalid_client_id | invalid_multipart,
     * 409 client_id_conflict, 413 photo_too_large, 415 unsupported_type —
     * all terminal — and 429 rate_limited, retryable like a transport error
     * or 5xx (the app's classifier, e.g. Staff `isFreeRetry`, ruling B8).
     *
     * NOTE (ruling B2): this puts the server code in `ApiError.code`; JSON
     * requests via `request()` put it in `ApiError.message` with `code` null.
     */
    uploadPhoto: async (
      file: File | Blob | RNFileLike,
      opts: { clientId: string; kind: 'in' | 'out' },
    ): Promise<AttendancePhotoResponse> => {
      const form = new FormData();
      form.append('client_id', opts.clientId);
      form.append('kind', opts.kind);
      form.append('file', file as Blob);
      const res = await req.raw('/staff/attendance/photos', { method: 'POST', body: form });
      if (!res.ok) {
        if (res.status === 401) await Promise.resolve(req.getStorage().set(null));
        let code: string | null = null;
        try {
          const payload = (await res.json()) as { error?: unknown };
          code = typeof payload.error === 'string' ? payload.error : null;
        } catch {
          /* non-JSON error body */
        }
        throw new ApiError(res.status, code, `Photo upload failed: ${res.status}`);
      }
      return (await res.json()) as AttendancePhotoResponse;
    },
  };
}

export function staffTrackingEndpoints(req: InternalRequest) {
  return {
    /** POST /staff/tracking/opt-in — record opt-in (audit only post-Phase-2; identity is JWT-side per fix). */
    optIn: (body?: Record<string, unknown>) =>
      req.request<{ ok: true }>('/staff/tracking/opt-in', {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
    /** POST /staff/tracking/opt-out — record opt-out. */
    optOut: () =>
      req.request<{ ok: true }>('/staff/tracking/opt-out', {
        method: 'POST',
      }),
  };
}

export interface StaffPositionFix {
  latitude: number;
  longitude: number;
  accuracyM?: number;
  altitudeM?: number;
  speedKmh?: number;
  headingDeg?: number;
  batteryPct?: number;
  fixTime: string;
  onShift?: boolean;
  clientId?: string;
  appVersion?: string;
}

export interface StaffPositionsIngestRequest {
  fixes: StaffPositionFix[];
}

export interface StaffPositionsIngestResponse {
  inserted: number;
  deduped: number;
  ids: number[];
}

export interface StaffLatestPosition {
  userId: string;
  name: string;
  phone: string | null;
  staffRole: StaffRole;
  lastFix: {
    latitude: number;
    longitude: number;
    accuracyM: number | null;
    speedKmh: number | null;
    fixTime: string;
    capturedAt: string;
  } | null;
  shift: { id: string; startedAt: string } | null;
  sos: { id: string; triggeredAt: string } | null;
}

export function staffPositionsEndpoints(req: InternalRequest) {
  return {
    /**
     * POST /staff/positions — ingest a batch of 1..100 fixes. Online callers
     * can omit clientId; replay callers MUST set a stable UUID per fix so the
     * server's partial-unique index dedupes a retry of the same payload.
     */
    create: (body: StaffPositionsIngestRequest) =>
      req.request<StaffPositionsIngestResponse>('/staff/positions', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /**
     * GET /staff/positions/latest — admin / manager / sultan only. One row per
     * staff member in the org with their freshest position + open shift +
     * active SOS. Polled at 10s by the People page + Live Map staff layer.
     */
    latest: () =>
      req.request<{ staff: StaffLatestPosition[] }>('/staff/positions/latest'),
  };
}

export interface StaffAlertRow {
  id: string;
  ruleId: string;
  ruleName: string;
  conditionType: string;
  createdAt: string;
  /** Interval-shaped events (PR #131). Null for edge events / older rows. */
  startedAt?: string | null;
  endedAt?: string | null;
  durationMs?: number | null;
  shape?: 'edge' | 'interval' | null;
  autoClosed?: boolean | null;
}

export interface StaffAlertDetail extends StaffAlertRow {
  conditionsSnapshot: unknown;
  positionData: unknown;
}

export interface StaffAlertsResponse {
  alerts: StaffAlertRow[];
  nextCursor: string | null;
}

export interface StaffRuleRow {
  id: string;
  name: string;
  conditions: Array<{ type: string; params?: Record<string, unknown> }>;
  cooldownMinutes: number;
}

export interface StaffRulesResponse {
  rules: StaffRuleRow[];
}

export function staffMyAlertsEndpoints(req: InternalRequest) {
  return {
    list: (opts?: { limit?: number; before?: string }) => {
      const params = new URLSearchParams();
      if (opts?.limit) params.set('limit', String(opts.limit));
      if (opts?.before) params.set('before', opts.before);
      const qs = params.toString();
      return req.request<StaffAlertsResponse>(
        `/staff/me/alerts${qs ? '?' + qs : ''}`,
      );
    },
    get: (id: string) =>
      req.request<StaffAlertDetail>(`/staff/me/alerts/${encodeURIComponent(id)}`),
  };
}

export function staffMyRulesEndpoints(req: InternalRequest) {
  return {
    list: () => req.request<StaffRulesResponse>('/staff/me/rules'),
  };
}

export interface AccountDeletionResult {
  deleted: true;
  /** True when this was the App Review demo login and it was re-seeded. */
  demoReseeded: boolean;
}

export function staffMyAccountEndpoints(req: InternalRequest) {
  return {
    /**
     * DELETE /staff/me/account — irreversibly delete the caller's own account.
     *
     * On success every JWT for this user is already dead server-side, so the
     * caller MUST clear local credentials and stop background location before
     * anything else tries a request — otherwise the app sits in a signed-in
     * shell issuing 401s. There is no undo and no confirmation step beyond
     * whatever the UI showed.
     */
    delete: () =>
      req.request<AccountDeletionResult>('/staff/me/account', { method: 'DELETE' }),
  };
}

export interface PoiVisitRow {
  poiId: string;
  poiName: string;
  category: string | null;
  arrivedAt: string;
  departedAt: string;
  dwellMs: number;
}

/** Mirror of server `Scorecard` (server/src/staff/scorecard.service.ts). Keep in sync. */
export interface StaffScorecard {
  period: { from: string; to: string; label: 'today' | 'week' | 'month' };
  workOutput: { completed: number; failed: number; completionRate: number | null };
  punctuality: { onTimeRate: number | null; lateCount: number; avgMinutesLate: number | null };
  effort: { distanceKm: number; activeHours: number };
  reliability: { checkInsCompleted: number; missedCheckIns: number | null; sosCount: number };
}

export function staffMyScorecardEndpoints(req: InternalRequest) {
  return {
    /** GET /staff/me/scorecard?period= — the authenticated worker's scorecard. */
    get: (period: 'today' | 'week' | 'month') =>
      req.request<StaffScorecard>(`/staff/me/scorecard?period=${period}`),
  };
}

export function staffMyVisitsEndpoints(req: InternalRequest) {
  return {
    /** GET /staff/me/poi-visits — the authenticated user's POI visit log. */
    list: (opts?: { from?: string; to?: string }) => {
      const params = new URLSearchParams();
      if (opts?.from) params.set('from', opts.from);
      if (opts?.to) params.set('to', opts.to);
      const qs = params.toString();
      return req.request<{ visits: PoiVisitRow[] }>(`/staff/me/poi-visits${qs ? `?${qs}` : ''}`);
    },
  };
}
